import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../auth/authContext';
import { listInstallers, getInstallerProjects } from '../installers/api';
import { listTasks } from '../tasks/api';
import { useIsMobile } from '../../hooks/useIsMobile';
const COLUMNS = [
    { status: 'assigned', label: 'Assigned', color: '#b45309', bg: 'rgba(251,191,36,0.1)' },
    { status: 'in_progress', label: 'In Progress', color: '#0369a1', bg: 'rgba(56,189,248,0.1)' },
    { status: 'completed', label: 'Completed', color: '#166534', bg: 'rgba(74,222,128,0.1)' },
];
function projectBoardStatus(status) {
    if (status === 'assigned')
        return 'assigned';
    if (status === 'in_progress')
        return 'in_progress';
    if (status === 'completed')
        return 'completed';
    return null;
}
function taskBoardStatus(status) {
    if (status === 'complete')
        return 'completed';
    if (status === 'in_progress' || status === 'blocked')
        return 'in_progress';
    return 'assigned';
}
export const TechnicianBoard = () => {
    const navigate = useNavigate();
    const { user, isLoading: authLoading } = useAuth();
    const isMobile = useIsMobile();
    const [installer, setInstaller] = useState(null);
    const [projects, setProjects] = useState([]);
    const [myTasks, setMyTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [openSections, setOpenSections] = useState(new Set(['assigned', 'in_progress']));
    useEffect(() => {
        // Wait for auth to finish loading
        if (authLoading)
            return;
        // If auth resolved but no email, mark not found rather than spinning forever
        if (!user?.email) {
            setNotFound(true);
            setLoading(false);
            return;
        }
        ;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                // Look up this user's installer record by email
                const result = await listInstallers({ email: user.email });
                if (result.items.length === 0) {
                    setNotFound(true);
                    return;
                }
                const me = result.items[0];
                setInstaller(me);
                // Load all projects assigned to this installer
                const projectResult = await getInstallerProjects(me.id);
                setProjects(projectResult.items);
                // Load internal tasks assigned to this installer
                const taskResult = await listTasks({ assignedInstallerId: me.id });
                setMyTasks(taskResult.items);
            }
            catch (e) {
                setError(e.message ?? 'Failed to load your jobs');
            }
            finally {
                setLoading(false);
            }
        })();
    }, [user?.email, authLoading]);
    const byStatus = (status) => {
        const projectItems = projects
            .filter((p) => projectBoardStatus(p.status) === status)
            .sort((a, b) => {
            if (a.scheduledDate && b.scheduledDate)
                return a.scheduledDate.localeCompare(b.scheduledDate);
            if (a.scheduledDate)
                return -1;
            if (b.scheduledDate)
                return 1;
            return b.updatedAt.localeCompare(a.updatedAt);
        })
            .map((p) => ({ kind: 'project', data: p }));
        const taskItems = myTasks
            .filter((t) => taskBoardStatus(t.status) === status)
            .map((t) => ({ kind: 'task', data: t }));
        return [...projectItems, ...taskItems];
    };
    const toggleSection = (status) => {
        setOpenSections((prev) => {
            const next = new Set(prev);
            if (next.has(status)) {
                next.delete(status);
            }
            else {
                next.add(status);
            }
            return next;
        });
    };
    if (loading) {
        return (_jsx(Layout, { children: _jsxs("div", { style: styles.loadingCenter, children: [_jsx("div", { style: styles.spinner }), _jsx("p", { style: styles.loadingText, children: "Loading your jobs\u2026" })] }) }));
    }
    if (notFound) {
        return (_jsx(Layout, { children: _jsxs("div", { style: styles.emptyState, children: [_jsx("div", { style: styles.emptyIcon, children: "\uD83D\uDC77" }), _jsx("h2", { style: styles.emptyTitle, children: "No Installer Profile Found" }), _jsx("p", { style: styles.emptyBody, children: user?.email
                            ? `Your account (${user.email}) isn't linked to an installer record yet. Ask an administrator to create your installer profile.`
                            : `Your account${user?.username ? ` (${user.username})` : ''} does not have an email address configured in Cognito. Ask an administrator to add an email to your user account, then log out and back in.` })] }) }));
    }
    if (error) {
        return (_jsx(Layout, { children: _jsx("div", { style: styles.errorBanner, children: error }) }));
    }
    const totalActive = projects.filter((p) => p.status !== 'completed').length +
        myTasks.filter((t) => t.status !== 'complete').length;
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsx("div", { style: styles.pageHeader, children: _jsxs("div", { children: [_jsx("h2", { style: styles.heading, children: "My Work" }), _jsxs("p", { style: styles.subheading, children: [installer?.firstName, " ", installer?.lastName, " \u00B7", ' ', totalActive === 0
                                        ? 'No active jobs'
                                        : `${totalActive} active job${totalActive !== 1 ? 's' : ''}`] })] }) }), isMobile ? (
                // Mobile: vertical collapsible sections
                _jsx("div", { style: styles.mobileStack, children: COLUMNS.map(({ status, label, color, bg }) => {
                        const col = byStatus(status);
                        const isOpen = openSections.has(status);
                        return (_jsxs("div", { style: styles.mobileSection, children: [_jsxs("button", { style: { ...styles.mobileSectionHeader, backgroundColor: bg }, onClick: () => toggleSection(status), children: [_jsxs("div", { style: styles.mobileSectionLeft, children: [_jsx("span", { style: { ...styles.columnLabel, color }, children: label }), _jsx("span", { style: { ...styles.columnCount, color, marginLeft: '10px' }, children: col.length })] }), _jsx("span", { style: { ...styles.mobileSectionToggle, color }, children: isOpen ? '▲' : '▼' })] }), isOpen && (_jsx("div", { style: styles.mobileSectionBody, children: col.length === 0 ? (_jsx("p", { style: styles.columnEmpty, children: "Nothing here" })) : (col.map((item) => item.kind === 'project' ? (_jsx(JobCard, { project: item.data, onOpen: () => navigate(`/field/${item.data.id}`), isMobile: true }, item.data.id)) : (_jsx(TaskCard, { task: item.data, onOpen: () => navigate(`/tasks/${item.data.id}`), isMobile: true }, item.data.id)))) }))] }, status));
                    }) })) : (
                // Desktop: 3-column grid with jobs and tasks combined
                _jsx("div", { style: styles.board, children: COLUMNS.map(({ status, label, color, bg }) => {
                        const col = byStatus(status);
                        return (_jsxs("div", { style: styles.column, children: [_jsxs("div", { style: { ...styles.columnHeader, backgroundColor: bg }, children: [_jsx("span", { style: { ...styles.columnLabel, color }, children: label }), _jsx("span", { style: { ...styles.columnCount, color }, children: col.length })] }), _jsx("div", { style: styles.columnBody, children: col.length === 0 ? (_jsx("p", { style: styles.columnEmpty, children: "Nothing here" })) : (col.map((item) => item.kind === 'project' ? (_jsx(JobCard, { project: item.data, onOpen: () => navigate(`/field/${item.data.id}`), isMobile: false }, item.data.id)) : (_jsx(TaskCard, { task: item.data, onOpen: () => navigate(`/tasks/${item.data.id}`), isMobile: false }, item.data.id)))) })] }, status));
                    }) }))] }) }));
};
const JobCard = ({ project, onOpen, isMobile }) => {
    const [hovered, setHovered] = useState(false);
    const formatDate = (d) => {
        if (!d)
            return null;
        const [year, month, day] = d.split('-');
        return `${month}/${day}/${year}`;
    };
    const isOverdue = project.scheduledDate &&
        project.status !== 'completed' &&
        new Date(project.scheduledDate) < new Date(new Date().toDateString());
    return (_jsxs("div", { style: {
            ...styles.card,
            transform: hovered ? 'translateY(-2px)' : 'none',
            boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.06)',
            cursor: 'pointer',
        }, onClick: onOpen, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsxs("div", { style: styles.cardTop, children: [_jsx("span", { style: styles.cardId, children: project.id }), project.priority === 'urgent' && _jsx("span", { style: styles.urgentBadge, children: "Urgent" })] }), _jsx("p", { style: styles.cardTitle, children: project.title }), _jsx("p", { style: styles.cardCustomer, children: project.customerName }), project.siteAddress && (_jsxs("p", { style: styles.cardAddress, children: [project.siteAddress.street, ", ", project.siteAddress.city, ", ", project.siteAddress.state] })), project.scheduledDate && (_jsxs("p", { style: { ...styles.cardDate, color: isOverdue ? '#c0392b' : '#86868b' }, children: [isOverdue ? 'Overdue · ' : '', formatDate(project.scheduledDate), project.scheduledStart && ` · ${project.scheduledStart}`] })), _jsx("button", { style: {
                    ...styles.openButton,
                    minHeight: isMobile ? '44px' : undefined,
                }, onClick: onOpen, tabIndex: -1, children: "Open Job \u2192" })] }));
};
function taskStatusLabel(status) {
    switch (status) {
        case 'pending':
        case 'unassigned':
            return 'Pending';
        case 'in_progress': return 'In Progress';
        case 'blocked': return 'Blocked';
        case 'complete': return 'Complete';
        default: return 'Pending';
    }
}
function taskStatusColor(status) {
    switch (status) {
        case 'complete': return '#248a3d';
        case 'in_progress': return '#0071e3';
        case 'blocked': return '#c0392b';
        case 'pending': return '#b45309';
        default: return '#86868b';
    }
}
function taskStatusBg(status) {
    switch (status) {
        case 'complete': return 'rgba(52,199,89,0.12)';
        case 'in_progress': return 'rgba(0,122,255,0.12)';
        case 'blocked': return 'rgba(255,59,48,0.12)';
        case 'pending': return 'rgba(255,149,0,0.12)';
        default: return 'rgba(142,142,147,0.15)';
    }
}
const TaskCard = ({ task, onOpen, isMobile }) => {
    const [hovered, setHovered] = useState(false);
    const formatDate = (d) => {
        if (!d)
            return null;
        const [year, month, day] = d.split('-');
        return `${month}/${day}/${year}`;
    };
    return (_jsxs("div", { style: {
            ...taskCardStyle,
            transform: hovered ? 'translateY(-2px)' : 'none',
            boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.06)',
            cursor: 'pointer',
            width: isMobile ? '100%' : '260px',
        }, onClick: onOpen, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }, children: _jsx("span", { style: {
                        display: 'inline-block',
                        padding: '2px 8px',
                        backgroundColor: taskStatusBg(task.status),
                        color: taskStatusColor(task.status),
                        borderRadius: '980px',
                        fontSize: '11px',
                        fontWeight: '700',
                    }, children: taskStatusLabel(task.status) }) }), _jsx("p", { style: { fontSize: '14px', fontWeight: '600', color: '#1d1d1f', margin: '0 0 4px 0', lineHeight: '1.3' }, children: task.name }), task.dueDate && (_jsxs("p", { style: { fontSize: '12px', color: '#86868b', margin: '0 0 8px 0' }, children: ["Due ", formatDate(task.dueDate)] })), _jsx("button", { style: {
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#5856d6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minHeight: isMobile ? '44px' : undefined,
                }, onClick: onOpen, tabIndex: -1, children: "Open Task \u2192" })] }));
};
const taskCardStyle = {
    backgroundColor: 'rgba(255,255,255,0.85)',
    borderRadius: '14px',
    padding: '14px 16px',
    border: '1px solid rgba(0,0,0,0.07)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
};
const styles = {
    pageHeader: {
        marginBottom: '28px',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        color: '#86868b',
        margin: 0,
    },
    board: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        alignItems: 'start',
    },
    column: {
        backgroundColor: 'rgba(255,255,255,0.4)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    },
    columnHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
    },
    columnLabel: {
        fontSize: '13px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    },
    columnCount: {
        fontSize: '13px',
        fontWeight: '700',
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    columnBody: {
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        minHeight: '80px',
    },
    columnEmpty: {
        fontSize: '13px',
        color: '#86868b',
        textAlign: 'center',
        padding: '20px 0',
        margin: 0,
    },
    card: {
        backgroundColor: 'rgba(255,255,255,0.85)',
        borderRadius: '14px',
        padding: '14px 16px',
        border: '1px solid rgba(0,0,0,0.07)',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    },
    cardTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '6px',
    },
    cardId: {
        fontSize: '11px',
        fontWeight: '600',
        color: '#86868b',
        fontFamily: 'monospace',
    },
    urgentBadge: {
        fontSize: '10px',
        fontWeight: '700',
        color: '#c0392b',
        backgroundColor: 'rgba(192,57,43,0.1)',
        padding: '2px 8px',
        borderRadius: '980px',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    cardTitle: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1d1d1f',
        margin: '0 0 4px 0',
        lineHeight: '1.3',
    },
    cardCustomer: {
        fontSize: '13px',
        color: '#0071e3',
        margin: '0 0 3px 0',
        fontWeight: '500',
    },
    cardAddress: {
        fontSize: '12px',
        color: '#86868b',
        margin: '0 0 3px 0',
    },
    cardDate: {
        fontSize: '12px',
        margin: '0 0 10px 0',
        fontWeight: '500',
    },
    openButton: {
        width: '100%',
        padding: '8px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    // Mobile stacked layout
    mobileStack: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    mobileSection: {
        backgroundColor: 'rgba(255,255,255,0.4)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    },
    mobileSectionHeader: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        border: 'none',
        cursor: 'pointer',
        boxSizing: 'border-box',
    },
    mobileSectionLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    mobileSectionToggle: {
        fontSize: '13px',
        fontWeight: '700',
    },
    mobileSectionBody: {
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    loadingCenter: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 0',
        gap: '16px',
    },
    spinner: {
        width: '36px',
        height: '36px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    loadingText: {
        color: '#86868b',
        fontSize: '15px',
        margin: 0,
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '80px 24px',
        textAlign: 'center',
        gap: '12px',
    },
    emptyIcon: {
        fontSize: '48px',
    },
    emptyTitle: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: 0,
    },
    emptyBody: {
        fontSize: '15px',
        color: '#86868b',
        maxWidth: '400px',
        margin: 0,
        lineHeight: '1.5',
    },
    errorBanner: {
        padding: '14px 18px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
    },
};
//# sourceMappingURL=TechnicianBoard.js.map